/**
 * Per-food nutrition scaling (quantity × grams-per-unit ÷ reference serving).
 * Aggregates live on the diet API; this module is for local editor preview only.
 */
import { getBaseNutrient } from "./nutrientDisplay";

export const COMPACT_NUTRIENT_KEYS = [
  "calories",
  "proteins",
  "carbs",
  "fats",
  "fiber",
  "sugars",
];

const normalizeText = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export function emptyCompactTotals() {
  return Object.fromEntries(COMPACT_NUTRIENT_KEYS.map((k) => [k, 0]));
}

const UNIT_STRING_KEYS = [
  "quantity_type",
  "quantity_type_description",
  "target_quantity_type",
  "target_quantity_type_description",
  "actual_quantity_type",
];

export function resolveUnitName(row, detail, dietCommonUtils = {}) {
  for (const key of UNIT_STRING_KEYS) {
    const val = row?.[key];
    if (typeof val === "string" && val.trim()) return val.trim();
  }

  const quantityTypes = dietCommonUtils?.quantity_types || [];
  if (row?.quantity_type_id && quantityTypes.length) {
    const hit = quantityTypes.find((q) => q.id === row.quantity_type_id);
    if (hit?.description) {
      const descNorm = normalizeText(hit.description);
      const mm = (detail?.measures || []).find(
        (m) => normalizeText(m.unit_option_name) === descNorm
      );
      if (mm?.unit_option_name) return mm.unit_option_name;
      return hit.description;
    }
  }

  return detail?.measures?.[0]?.unit_option_name || null;
}

export function gramsPerUnit(detail, unitName) {
  const measures = detail?.measures || [];
  if (!measures.length || unitName == null || unitName === "") return null;

  const norm = normalizeText(unitName);
  const isGramAlias = (n) => n === "g" || n === "gram" || n === "grams";

  let unitDef = measures.find((m) => m.unit_option_name === unitName);
  if (!unitDef && norm) {
    unitDef = measures.find((m) => normalizeText(m.unit_option_name) === norm);
  }
  // Real synonyms only — "Gram" / "grams" ↔ measure named "g".
  if (!unitDef && isGramAlias(norm)) {
    unitDef = measures.find((m) =>
      isGramAlias(normalizeText(m.unit_option_name))
    );
  }
  if (unitDef?.basic_unit_measure == null) return null;
  const grams = Number(unitDef.basic_unit_measure);
  return Number.isFinite(grams) ? grams : null;
}

export function scaleNutrition(detail, quantity, unitName) {
  if (!detail) return emptyCompactTotals();

  const gramsPerUnitVal = gramsPerUnit(detail, unitName);
  // No silent unit substitution — unmatched units return zeros; callers that
  // have API scaled_nutrition should use resolveScaledNutrition instead.
  if (gramsPerUnitVal == null) return emptyCompactTotals();

  const baseFor = Number(detail.calories_calculated_for || 100) || 100;
  const totalGrams = Number(quantity || 0) * gramsPerUnitVal;
  const scale = (v) =>
    parseFloat(((Number(v) * totalGrams) / baseFor).toFixed(2));

  return {
    proteins: scale(getBaseNutrient(detail, "proteins")),
    carbs: scale(getBaseNutrient(detail, "carbs")),
    fats: scale(getBaseNutrient(detail, "fats")),
    fiber: scale(getBaseNutrient(detail, "fiber")),
    sugars: scale(getBaseNutrient(detail, "sugars")),
    calories: scale(getBaseNutrient(detail, "calories")),
  };
}

function foodQuantity(food) {
  if (food?.quantity != null) return food.quantity;
  if (food?.target_quantity != null) return food.target_quantity;
  if (food?.actual_quantity != null) return food.actual_quantity;
  return 1;
}

function foodDetail(food) {
  // Only recipe_details has `nutrients` for scaleNutrition. food_details uses a
  // different shape (`nutrition`) and must not be treated as a scaling source.
  return food?.recipe_details || null;
}

/** Normalize a compact/API scaled_nutrition object; null if empty. */
export function compactFromScaledNutrition(scaled) {
  if (!scaled || typeof scaled !== "object") return null;
  const calories = Number(scaled.calories ?? scaled.total_energy_kcal ?? 0);
  const proteins = Number(scaled.proteins ?? scaled.protein ?? scaled.total_protein ?? 0);
  const carbs = Number(
    scaled.carbs ?? scaled["total_carbohydrate,_by_difference"] ?? 0
  );
  const fats = Number(scaled.fats ?? scaled["total_total_lipid_(fat)"] ?? 0);
  const fiber = Number(scaled.fiber ?? scaled["total_fiber,_total_dietary"] ?? 0);
  const sugars = Number(scaled.sugars ?? scaled.sugar ?? scaled["total_sugars,_total"] ?? 0);
  if (!calories && !proteins && !carbs && !fats && !fiber && !sugars) return null;
  return { calories, proteins, carbs, fats, fiber, sugars };
}

/**
 * Use API scaled_nutrition when present with calories; otherwise scale from
 * recipe_details for the given quantity/unit. No guessed units.
 */
export function resolveScaledNutrition(row, detail, quantity, unitName) {
  const fromApi = compactFromScaledNutrition(row?.scaled_nutrition);
  if (fromApi && Number(fromApi.calories) > 0) return fromApi;

  if (detail?.nutrients) {
    const fromDetail = scaleNutrition(detail, quantity, unitName);
    if (Number(fromDetail.calories) > 0) return fromDetail;
  }
  // Keep API macros (even if calories are 0) over invented zeros when detail
  // could not be scaled for this unit.
  return fromApi || emptyCompactTotals();
}

/**
 * Recompute a food row's macros for an in-progress quantity/unit edit. Used
 * both for the optimistic save (DietMealSlotEditor.handleUpdateRecipeItem)
 * and the live preview in the edit modal, so the two can never disagree.
 * `row` supplies the previous quantity/scaled_nutrition for the fallback.
 */
export function scaleFoodForQuantityEdit(row, detail, quantity, unitName) {
  let scaled = resolveScaledNutrition(
    { quantity, quantity_type: unitName },
    detail,
    quantity,
    unitName
  );
  // If detail/unit cannot scale yet, keep previous macros proportionally so
  // the card/preview never flashes 0 kcal mid-edit.
  if (
    !(Number(scaled?.calories) > 0) &&
    row?.scaled_nutrition &&
    Number(row.quantity) > 0
  ) {
    const ratio = quantity / Number(row.quantity);
    scaled = Object.fromEntries(
      Object.entries(row.scaled_nutrition).map(([k, v]) => [
        k,
        typeof v === "number" ? Math.round(v * ratio * 100) / 100 : v,
      ])
    );
  }
  return scaled;
}

/** Compact scaled-nutrition object -> display-ready 2-decimal strings. */
export function formatCompactNutrition(scaled) {
  const fmt = (v) => Number(v ?? 0).toFixed(2);
  return {
    protein: fmt(scaled?.proteins ?? scaled?.protein),
    carbs: fmt(scaled?.carbs),
    fats: fmt(scaled?.fats),
    fiber: fmt(scaled?.fiber),
    sugar: fmt(scaled?.sugars ?? scaled?.sugar),
    calories: fmt(scaled?.calories),
  };
}

/** True when a live/API nutrition summary has any non-zero option calories. */
export function nutritionSummaryHasCalories(summary) {
  if (!summary || typeof summary !== "object") return false;
  const byOpt = summary.totalsByOption || summary.byOption || {};
  return Object.values(byOpt).some((t) => Number(t?.calories) > 0);
}

function optionNumber(option) {
  const title = option?.title || option?.meal_option_title || "";
  const match = String(title).match(/\d+/);
  return match ? match[0] : "1";
}

function mealLabel(meal) {
  return (
    meal?.meal_type_description ||
    meal?.meal_type ||
    meal?.tracking_meal_type ||
    ""
  );
}

function addCompact(totals, addition = {}) {
  totals.calories += Number(addition.calories ?? 0);
  totals.proteins += Number(addition.proteins ?? addition.protein ?? 0);
  totals.carbs += Number(addition.carbs ?? 0);
  totals.fats += Number(addition.fats ?? 0);
  totals.fiber += Number(addition.fiber ?? 0);
  totals.sugars += Number(addition.sugars ?? addition.sugar ?? 0);
}

function roundCompact(totals) {
  COMPACT_NUTRIENT_KEYS.forEach((k) => {
    totals[k] = parseFloat(Number(totals[k] || 0).toFixed(2));
  });
  return totals;
}

/** Live editor preview only — saved plans/templates use API nutrition_summary. */
export function computeMealStructureNutrition(meals = [], dietCommonUtils = {}) {
  const totalsByOption = {};
  const byMealType = {};
  const rowCaloriesMap = {};

  (meals || []).forEach((meal) => {
    const label = mealLabel(meal);
    (meal.meal_options || []).forEach((option) => {
      const optNum = optionNumber(option);
      const rowKey = `${label}|Option ${optNum}`;

      if (!totalsByOption[optNum]) totalsByOption[optNum] = emptyCompactTotals();
      if (!byMealType[label]) byMealType[label] = {};
      if (!byMealType[label][optNum]) byMealType[label][optNum] = emptyCompactTotals();
      if (rowCaloriesMap[rowKey] == null) rowCaloriesMap[rowKey] = 0;

      (option.foods || []).forEach((food) => {
        const fid =
          food?.food_id || food?.target_food_id || food?.actual_food_id;
        if (!fid) return;
        const detail = foodDetail(food);
        const unit = resolveUnitName(food, detail, dietCommonUtils);
        const scaled = resolveScaledNutrition(
          food,
          detail,
          foodQuantity(food),
          unit
        );
        if (!scaled) return;

        addCompact(totalsByOption[optNum], scaled);
        addCompact(byMealType[label][optNum], scaled);
        rowCaloriesMap[rowKey] += Number(scaled.calories || 0);
      });
    });
  });

  const averageAcrossOptions = emptyCompactTotals();
  const optionKeys = Object.keys(totalsByOption);
  if (optionKeys.length) {
    optionKeys.forEach((k) => addCompact(averageAcrossOptions, totalsByOption[k]));
    const n = optionKeys.length;
    COMPACT_NUTRIENT_KEYS.forEach((key) => {
      averageAcrossOptions[key] = parseFloat(
        (averageAcrossOptions[key] / n).toFixed(2)
      );
    });
  }

  Object.keys(totalsByOption).forEach((k) => roundCompact(totalsByOption[k]));
  const rowTotalsMap = {};
  Object.keys(byMealType).forEach((ml) => {
    Object.keys(byMealType[ml]).forEach((opt) => {
      roundCompact(byMealType[ml][opt]);
      rowTotalsMap[`${ml}|Option ${opt}`] = { ...byMealType[ml][opt] };
    });
  });
  Object.keys(rowCaloriesMap).forEach((k) => {
    rowCaloriesMap[k] = parseFloat(Number(rowCaloriesMap[k]).toFixed(2));
  });

  return {
    totalsByOption,
    byOption: totalsByOption,
    rowCaloriesMap,
    rowTotalsMap,
    byMealType,
    averageAcrossOptions,
  };
}
